import { useLayoutEffect, useMemo, useRef, useSyncExternalStore } from "react";
/** Subscribe to a bindable inside React component state. */
export const useBindableValue = (bindable) => useSyncExternalStore(bindable.subscribe, bindable.current);
/**
 * Derive a new bindable from a target bindable, managing its lifecycle in React.
 * Recreate the instance when the target or provided dependencies change.
 *
 * @example
 * const chartApi = useBind(root, element => [createChart(element, options), () => chart.remove()], { oneTime: true });
 */
export const useBind = (target, 
/** The factory function to create the value instance */
factory, options) => {
    const factoryRef = useRef(factory);
    factoryRef.current = factory;
    const next = useMemo(() => bind({
        target: target,
        create: (a) => factoryRef.current(a),
        oneTime: options?.oneTime,
    }), [target, options?.oneTime, ...(options?.deps ?? [])]);
    const value = useBindableValue(target);
    // biome-ignore lint/correctness/useExhaustiveDependencies: We want to re-register if the target changes
    useLayoutEffect(() => next.render(), [next, value]);
    return next;
};
/** Manage the lifecycle of a derived bindable relative to its target. */
const bind = (p) => {
    let value = null;
    let destroyed = false;
    let timeout;
    const subs = new Set();
    /** When the target is already destroyed, we dont call the cleanup function */
    const reset = () => {
        value = null;
        destroyed = true;
        subs.forEach((sub) => sub(value?.[0] ?? null));
    };
    const render = () => {
        if (timeout)
            clearTimeout(timeout);
        const target = p.target.current();
        if (target == null) {
            if (value)
                reset();
            return;
        }
        // If this is a one time bind the value might already exist
        if (value === null) {
            destroyed = false;
            const next = p.create(target) ?? null;
            if (typeof next === "function")
                value = [null, next];
            else
                value = next;
            subs.forEach((sub) => sub(value?.[0] ?? null));
        }
        return () => {
            // If the target binding is already destroyed, we don't need to unbind
            if (p.target.destroyed())
                return reset();
            // When we dont want to destroy and recreate on every render, we set a timeout and wait for the next tick
            // if the component is unmounted the cleanup will run after the next tick
            if (p.oneTime) {
                timeout = setTimeout(() => {
                    // If the target has changed, or is destroyed we don't need to unbind
                    if (value)
                        value[1]();
                    reset();
                }, 1);
                return;
            }
            if (value)
                value[1]();
            reset();
        };
    };
    return {
        render,
        current: () => {
            if (destroyed || p.target.destroyed())
                return null;
            return value?.[0] ?? null;
        },
        destroyed: () => destroyed,
        subscribe: (sub) => {
            subs.add(sub);
            return () => subs.delete(sub);
        },
    };
};
/** Create a bindable that always returns null and never updates. */
export const emptyBindable = () => ({
    render: () => void null,
    current: () => null,
    subscribe: () => () => void null,
    destroyed: () => false,
});
/** Extend a bindable with the ability to swap the upstream source. */
export const slot = () => {
    let inner = null;
    const subs = new Set();
    let parentSub = null;
    let last = null;
    const notify = () => {
        const next = inner?.current();
        if (next === last)
            return;
        last = next ?? null;
        subs.forEach((sub) => sub());
    };
    const subParent = () => {
        if (!parentSub || !inner || subs.size === 0)
            return;
        parentSub = inner.subscribe(() => notify());
    };
    return {
        bind: (bindable) => {
            if (inner === bindable)
                return;
            inner = bindable;
            notify();
            subParent();
        },
        render: () => { },
        current: () => inner?.current() ?? null,
        destroyed: () => inner?.destroyed() ?? true,
        subscribe: (sub) => {
            subs.add(sub);
            subParent();
            return () => {
                subs.delete(sub);
                if (subs.size > 0)
                    return;
                if (parentSub)
                    parentSub();
                parentSub = null;
            };
        },
    };
};
/** Create a bindable tied to a DOM element reference accessor. */
export const bindableElement = () => {
    let value = null;
    let destroyed = false;
    // eslint-disable-next-line @typescript-eslint/no-unused-vars -- this is incremented, but... why?
    let version = 0;
    const subs = new Set();
    return {
        current: () => value,
        destroyed: () => destroyed,
        render: () => void null,
        subscribe: (sub) => {
            sub();
            subs.add(sub);
            return () => subs.delete(sub);
        },
        ref: (element) => {
            value = element;
            if (element == null)
                destroyed = true;
            else {
                destroyed = false;
                version++;
            }
            for (const sub of subs)
                sub();
        },
    };
};
//# sourceMappingURL=index.js.map